/**
 * Every kind of notification the helpdesk sends. In-app notifications are
 * always on; email and push can be switched off per type in each person's
 * settings. `email: false` means the type never sends email at all.
 */
import { z } from 'zod';

export type NotificationAudience = 'staff' | 'it';

export interface NotificationTypeInfo {
  label: string;
  description: string;
  audience: NotificationAudience;
  /** Whether this type can send email (push is available for every type). */
  email: boolean;
}

export const NOTIFICATION_TYPES = {
  ticket_reply: {
    label: 'Replies',
    description: 'Someone replies on a ticket you reported or are handling.',
    audience: 'staff',
    email: true,
  },
  ticket_status: {
    label: 'Ticket progress',
    description: 'Your ticket is being worked on, needs your input, or is resolved.',
    audience: 'staff',
    email: true,
  },
  ticket_created: {
    label: 'New tickets',
    description: 'A new ticket is submitted.',
    audience: 'it',
    email: true,
  },
  ticket_requester_reply: {
    label: 'Requester replies',
    description: 'The person who reported a ticket replies or reopens it.',
    audience: 'it',
    email: true,
  },
  ticket_assigned: {
    label: 'Assigned to me',
    description: 'A ticket is assigned to you.',
    audience: 'it',
    email: true,
  },
  ticket_note: {
    label: 'Internal notes',
    description: 'A colleague adds an internal note to a ticket assigned to you.',
    audience: 'it',
    email: false,
  },
  sla_breached: {
    label: 'Overdue tickets',
    description: 'A ticket passes its response deadline.',
    audience: 'it',
    email: true,
  },
  feedback_received: {
    label: 'Feedback',
    description: 'A requester rates the service on a ticket.',
    audience: 'it',
    email: true,
  },
  daily_summary: {
    label: 'Morning summary',
    description: 'When the office opens: what came in overnight and what’s still open.',
    audience: 'it',
    email: true,
  },
  maintenance_due: {
    label: 'Planned maintenance',
    description: 'Planned maintenance (e.g. printer servicing) is due in a few days.',
    audience: 'it',
    email: true,
  },
} as const satisfies Record<string, NotificationTypeInfo>;

export type NotificationType = keyof typeof NOTIFICATION_TYPES;
export const NOTIFICATION_TYPE_KEYS = Object.keys(NOTIFICATION_TYPES) as NotificationType[];

export type Channel = 'email' | 'push';

export const notificationPrefsSchema = z.partialRecord(
  z.enum(NOTIFICATION_TYPE_KEYS as [NotificationType, ...NotificationType[]]),
  z.object({ email: z.boolean().optional(), push: z.boolean().optional() }),
);
export type NotificationPrefsInput = z.infer<typeof notificationPrefsSchema>;

/** Channels default to on; only an explicit `false` turns one off. */
export function channelEnabled(
  prefs: Partial<Record<string, { email?: boolean; push?: boolean }>>,
  type: NotificationType,
  channel: Channel,
): boolean {
  if (channel === 'email' && !NOTIFICATION_TYPES[type].email) return false;
  return prefs[type]?.[channel] !== false;
}

export const pushSubscriptionSchema = z.object({
  endpoint: z.url().max(1000),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
});
